import React, { FC, useMemo } from 'react';
import { Empty, Table, Typography } from 'antd';
import { styled } from 'styled-components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LoadLogEntry, VehicleLogEntry } from '@/types';
import { useTranslate } from '@/translations';
import { timeConverter } from '@/utils/helpers';
import { FORMAT_FULL_TIME_DAYJS } from '@/utils/constants';
import { LOAD_LANE_STATUS_CONFIG } from '@/modules/loadlanes/constants';
import { NotRecorded } from '@/modules/TransportMovement/components/NotRecorded';
import { TransportMovementLogsProps } from './types';

const typeTone = (transType: string) => {
  const type = transType?.toLowerCase();
  if (type === 'vin remove') return LOAD_LANE_STATUS_CONFIG.overdue;
  if (type === 'vin added') return LOAD_LANE_STATUS_CONFIG.onTime;
  return LOAD_LANE_STATUS_CONFIG.vacant;
};

const TypePill = styled.span<{ $color: string; $bg: string }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`;

const Section = styled.section`
  & + & {
    margin-top: 24px;
  }
`;

const SectionLabel = styled.h3`
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.lightText};
`;

const newestFirst = <T extends { transDate: string }>(logs: T[]) =>
  logs
    .map((log) => ({ log, time: dayjs.utc(log.transDate).valueOf() }))
    .sort((a, b) => b.time - a.time)
    .map(({ log }) => log);

export const TransportMovementLogs: FC<TransportMovementLogsProps> = ({
  loadLogs,
  vehicleLogs,
}) => {
  const { t } = useTranslate();

  const sortedLoadLogs = useMemo(() => newestFirst(loadLogs), [loadLogs]);
  const sortedVehicleLogs = useMemo(
    () => newestFirst(vehicleLogs),
    [vehicleLogs],
  );

  const { loadColumns, vehicleColumns } = useMemo(() => {
    const shared = [
      {
        dataIndex: 'transType',
        title: t('tsEventType'),
        width: 140,
        render: (value: string) => {
          if (!value) return <NotRecorded />;
          const { color, bg } = typeTone(value);
          return (
            <TypePill $color={color} $bg={bg}>
              {value}
            </TypePill>
          );
        },
      },
      {
        dataIndex: 'transDescription',
        title: t('tsEventDescription'),
        render: (value: string) => value || <NotRecorded />,
      },
      {
        dataIndex: 'transDate',
        title: t('tsEventTime'),
        width: 170,
        render: (value: string) =>
          timeConverter(value, FORMAT_FULL_TIME_DAYJS) ?? <NotRecorded />,
      },
    ];
    const vinColumn = {
      dataIndex: 'serialNumber',
      title: t('vin'),
      width: 190,
      render: (value: string) =>
        value ? (
          <Typography.Text copyable>{value}</Typography.Text>
        ) : (
          <NotRecorded />
        ),
    };
    return {
      loadColumns: shared as ColumnsType<LoadLogEntry>,
      vehicleColumns: [vinColumn, ...shared] as ColumnsType<VehicleLogEntry>,
    };
  }, [t]);

  const emptyText = (description: string) => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
  );

  return (
    <>
      <Section>
        <SectionLabel>{t('tsLoadActivity')}</SectionLabel>
        <Table
          dataSource={sortedLoadLogs}
          columns={loadColumns}
          rowKey={(log) =>
            `load-${log.movementTransIndex ?? `${log.transDate}-${log.transType}`}`
          }
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: emptyText(t('tsNoLoadActivity')) }}
        />
      </Section>
      <Section>
        <SectionLabel>{t('tsVehicleActivity')}</SectionLabel>
        <Table
          dataSource={sortedVehicleLogs}
          columns={vehicleColumns}
          rowKey={(log) =>
            `vehicle-${
              log.movementStockTransIndex ??
              `${log.serialNumber}-${log.transDate}-${log.transType}`
            }`
          }
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: emptyText(t('tsNoVehicleActivity')) }}
        />
      </Section>
    </>
  );
};
